import { useState, useCallback, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from '../lib/axios';
import { CalendarClock, RefreshCw, ChevronLeft, ChevronRight, MapPin, User, GripVertical, AlertTriangle, Download, Loader2 } from 'lucide-react';
import Layout from '../components/Layout';
import { useLocationContext } from '../context/LocationContext';

/** Stage colours, matching the technician calendar. */
const STAGE_DOT = {
  amc: '#1c6635',
  collection: '#ec4899',
  washing: '#0ea5e9',
  delivery: '#8b5cf6',
  other: '#94a3b8',
};

const stageOf = (text = '') => {
  const c = text.toLowerCase();
  if (c.includes('collection')) return 'collection';
  if (c.includes('clean') || c.includes('wash')) return 'washing';
  if (c.includes('delivery')) return 'delivery';
  if (c.includes('amc')) return 'amc';
  return 'other';
};

/**
 * The booking's jobs as display rows, earliest first: the AMC visit plus each
 * child job the server sent. Child times come from Zuper, not from the slot
 * template, so a stage someone moved by hand reads as it really is.
 */
function stageRows(booking, timings) {
  const rows = (booking.child_jobs || []).map((c) => {
    const stage = stageOf(`${c.title || ''} ${c.category || ''}`);
    return {
      key: c.job_uid,
      sort: c.start || '',
      dot: STAGE_DOT[stage],
      label: stage === 'other' ? (c.category || 'Job') : { collection: 'Collection', washing: 'Cleaning', delivery: 'Delivery', amc: 'AMC' }[stage],
      time: [c.start_label, c.end_label].filter(Boolean).join('-') || '—',
      technician: c.technician || null,
      wo: c.work_order_number || null,
      isParent: false,
    };
  });
  // Children carry an absolute timestamp; the AMC window is a local "14:00"
  // from the slot template. Build the same absolute key for it or the two sort
  // four hours apart and delivery lands above the visit it follows.
  const amcSort = (() => {
    try {
      return new Date(`${booking.scheduled_date}T${timings.amc?.start || '00:00'}:00`).toISOString();
    } catch {
      return `${booking.scheduled_date}T${timings.amc?.start || '00:00'}`;
    }
  })();
  rows.push({
    key: 'amc',
    sort: amcSort,
    dot: STAGE_DOT.amc,
    label: 'AMC',
    time: `${formatTime12(timings.amc?.start)}-${formatTime12(timings.amc?.end)}`,
    technician: booking.assigned_mechanic_name || null,
    isParent: true,
  });
  return rows.sort((a, b) => String(a.sort).localeCompare(String(b.sort)));
}

function formatTime12(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
}

function formatDateDisplay(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

// Draggable slot card
function SlotCard({ slot, mechKey, slotLabel, onDragStart, isDragging }) {
  const booking = slot.booking;
  const timings = slot.timings;
  const cardRef = useRef(null);

  if (!booking) {
    // Empty is not the same as bookable. A square with nobody free to collect or
    // wash the buggy cannot take a booking, and saying "Available" here is what
    // sent staff to the booking page to be refused. The blocked reason names the
    // stage, because "full" is not something anyone can act on.
    const blocked = slot.staffing && slot.staffing.ok === false;
    return (
      <div
        className={`border-2 border-dashed rounded-xl p-2.5 min-h-[80px] flex flex-col justify-center gap-0.5 ${
          blocked ? 'border-amber-200 bg-amber-50/40 items-stretch' : 'border-brand-light/80 bg-gray-50/30 items-center text-center'
        }`}
        data-drop-target="true"
        data-mech-key={mechKey}
        data-position={slot.position}
        data-slot-label={slotLabel}
        title={blocked
          ? `Nobody free for ${slot.staffing.blockedBy} — ` +
            (slot.staffing.conflicts || [])
              .map((c) => `${c.technician}: #${c.work_order_number || '-'} ${c.category} ${c.time}`)
              .join(' · ')
          : undefined}
      >
        {blocked ? (
          /* Plain schedule, not a design: who is needed for this square and what
             they are already booked on. The point is to answer "why is this
             busy" at a glance, so it reads as a list rather than as cards. */
          <div className="w-full text-left">
            <p className="text-[11px] font-semibold text-amber-700/90 mb-1">
              Blocked · {slot.staffing.blockedBy}
            </p>
            {(slot.staffing.stages || []).map((st) => (
              <div key={st.stage} className="mb-1 last:mb-0">
                <p className="text-[9px] font-semibold uppercase tracking-wide text-brand-neutral/70">
                  {st.stage} {st.window}
                </p>
                {st.people.map((p, i) => (
                  <div key={i} className="text-[10px] leading-snug text-brand-neutral/80">
                    {p.free ? (
                      <span>{p.name} — free</span>
                    ) : (
                      p.jobs.map((j, k) => (
                        <div key={k} className="flex items-baseline gap-1.5">
                          <span className="truncate max-w-[84px]">{k === 0 ? p.name : ''}</span>
                          <span className="font-mono shrink-0">#{j.work_order_number || '-'}</span>
                          <span className="truncate flex-1">{j.category}</span>
                          <span className="tabular-nums shrink-0">{j.time}</span>
                        </div>
                      ))
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : (
          <span className="text-xs text-brand-neutral/40 font-medium">Available</span>
        )}
      </div>
    );
  }

  // When this card is being dragged, show a placeholder outline
  if (isDragging) {
    return (
      <div
        className="border-2 border-dashed border-brand-primary/40 rounded-xl p-3 bg-brand-primary/5 min-h-[80px] flex items-center justify-center"
        data-drop-target="true"
        data-mech-key={mechKey}
        data-position={slot.position}
        data-slot-label={slotLabel}
      >
        <span className="text-xs text-brand-primary/40 font-medium">Moving...</span>
      </div>
    );
  }

  return (
    <div
      ref={cardRef}
      draggable
      onDragStart={(e) => {
        // Create a styled drag preview clone
        const rect = cardRef.current.getBoundingClientRect();
        const clone = cardRef.current.cloneNode(true);
        clone.style.position = 'absolute';
        clone.style.top = '-9999px';
        clone.style.left = '-9999px';
        clone.style.width = `${rect.width}px`;
        clone.style.opacity = '0.9';
        clone.style.transform = 'rotate(2deg)';
        clone.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
        clone.style.borderRadius = '12px';
        clone.style.border = '2px solid var(--brand-primary, #2d6a4f)';
        clone.style.background = 'white';
        document.body.appendChild(clone);
        e.dataTransfer.setDragImage(clone, rect.width / 2, 20);
        // Clean up clone after a frame
        requestAnimationFrame(() => {
          setTimeout(() => document.body.removeChild(clone), 0);
        });
        onDragStart(e, booking, mechKey, slot.position, slotLabel);
      }}
      className="rounded-xl overflow-hidden border-2 border-brand-light bg-white shadow-md hover:shadow-lg transition-shadow cursor-grab active:cursor-grabbing group"
      data-drop-target="true"
      data-mech-key={mechKey}
      data-position={slot.position}
      data-slot-label={slotLabel}
    >
      {stageRows(booking, timings).map((row) => (
        row.isParent ? (
          /* The booking itself: the card that carries the customer and the one
             the drag handle lives on. */
          <div key={row.key}>
            {/* White, unlike the stages: the booking is the body of the card,
                not another layer wrapped around it. */}
            <div className="flex items-center justify-between px-2.5 py-1.5 bg-white border-b border-brand-light/50">
              <span className="text-[10px] font-bold text-brand-dark tracking-wide">AMC</span>
              <span className="text-[10px] font-semibold text-brand-dark tabular-nums">{row.time}</span>
            </div>
            <div className="p-2.5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-brand-dark truncate">{booking.customer_name}</p>
                  <p className="text-[11px] text-brand-neutral mt-0.5">{booking.customer_phone || '—'}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <span className="text-[10px] font-mono text-brand-neutral bg-brand-light/40 px-1.5 py-0.5 rounded">
                    #{booking.work_order_number || '—'}
                  </span>
                  <GripVertical className="w-3.5 h-3.5 text-brand-neutral/30 group-hover:text-brand-neutral/60 transition-colors" />
                </div>
              </div>
              {(booking.asset_name || booking.asset_serial_number) && (
                <p className="text-[11px] text-brand-neutral mt-1.5 truncate">
                  {[booking.asset_name, booking.asset_serial_number].filter(Boolean).join(' · ')}
                </p>
              )}
              <div className="flex items-center justify-between mt-1.5">
                <span className="text-[10px] text-brand-neutral truncate">
                  {row.technician || 'No technician'}
                </span>
                <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${
                  booking.scheduled_by === 'zuper_sync'
                    ? 'bg-blue-50 text-blue-600'
                    : 'bg-emerald-50 text-emerald-600'
                }`}>
                  {booking.scheduled_by === 'zuper_sync' ? 'Zuper' : 'Booked'}
                </span>
              </div>
            </div>
          </div>
        ) : (
          /* A stage: its own card above or below the booking, in the order it
             actually happens, so the chain reads down the column as the day
             runs. Moves with the booking; it is not dragged on its own. */
          /* One solid block per stage: the whole layer is its colour, so a
             booking reads as coloured bands around the white AMC body rather
             than as alternating stripes. */
          <div key={row.key} className="px-2.5 py-1" style={{ background: row.dot }}>
            <div className="flex items-center justify-between gap-2">
              <span className="text-[9px] font-bold text-white tracking-wide">{row.label}</span>
              <span className="text-[9px] font-semibold text-white tabular-nums">{row.time}</span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] text-white/85 truncate">
                {row.technician || <span className="font-semibold text-white">No technician</span>}
              </span>
              {row.wo && <span className="text-[9px] font-mono text-white/70 shrink-0">#{row.wo}</span>}
            </div>
          </div>
        )
      ))}
    </div>
  );
}

export default function BookedSlots() {
  const [dateOffset, setDateOffset] = useState(0);
  const { selectedLocationId, currentLocation } = useLocationContext();
  const queryClient = useQueryClient();

  // Drag state
  const [dragData, setDragData] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  // Toast state for reassignment feedback
  const [toast, setToast] = useState(null);

  const date = new Date();
  date.setDate(date.getDate() + dateOffset);
  const dateStr = date.toISOString().split('T')[0];

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['booked-slots', dateStr, selectedLocationId],
    queryFn: async () => {
      const { data } = await axios.get(`/api/booked-slots?date=${dateStr}`);
      return data;
    },
  });

  const reassignMutation = useMutation({
    mutationFn: async (payload) => {
      const { data } = await axios.post('/api/booked-slots/reassign', payload);
      return data;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['booked-slots'] });
      const waMsg = result.whatsappStatus === 'sent'
        ? 'Confirmation sent to customer'
        : result.whatsappStatus === 'skipped'
          ? 'No phone — confirmation skipped'
          : `Confirmation failed: ${result.whatsappStatus}`;
      setToast({
        type: result.whatsappStatus === 'sent' ? 'success' : 'warning',
        message: `Slot moved: ${result.from.mechanic || '?'} → ${result.to.mechanic || '?'} (${result.to.slot} #${result.to.position}). ${waMsg}`,
      });
      setTimeout(() => setToast(null), 5000);
    },
    onError: (err) => {
      setToast({
        type: 'error',
        message: err.response?.data?.error || err.message,
      });
      setTimeout(() => setToast(null), 4000);
    },
  });

  // Compute weekOffset from dateStr for sync API
  const getWeekOffset = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(dateStr + 'T00:00:00');
    const dayOfWeek = today.getDay();
    const mondayThisWeek = new Date(today);
    mondayThisWeek.setDate(today.getDate() - ((dayOfWeek + 6) % 7));
    const diffDays = Math.floor((target - mondayThisWeek) / (1000 * 60 * 60 * 24));
    return Math.floor(diffDays / 7);
  }, [dateStr]);

  const syncMutation = useMutation({
    mutationFn: async () => {
      const { data } = await axios.post('/api/reminders/sync', { weekOffset: getWeekOffset() });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['booked-slots'] });
      setToast({ type: 'success', message: 'Synced from Zuper — slot grid refreshed' });
      setTimeout(() => setToast(null), 4000);
    },
    onError: (err) => {
      setToast({ type: 'error', message: `Sync failed: ${err.response?.data?.error || err.message}` });
      setTimeout(() => setToast(null), 4000);
    },
  });

  const slotGrid = data?.slotGrid || {};
  const mechanics = data?.mechanics || [];
  const unassignedBookings = data?.unassignedBookings || [];
  const totalBookings = data?.totalBookings || 0;

  // Drag handlers
  const handleDragStart = useCallback((e, booking, fromMechKey, fromPosition, fromSlotLabel) => {
    setDragData({ booking, fromMechKey, fromPosition, fromSlotLabel });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', booking.job_uid);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    // Find the closest drop target
    const target = e.target.closest('[data-drop-target]');
    if (target) {
      const mechKey = target.getAttribute('data-mech-key');
      const position = parseInt(target.getAttribute('data-position'), 10);
      const slotLabel = target.getAttribute('data-slot-label');
      setDropTarget({ mechKey, position, slotLabel });
    }
  }, []);

  const handleDragLeave = useCallback((e) => {
    const related = e.relatedTarget;
    if (!related || !related.closest?.('[data-drop-target]')) {
      setDropTarget(null);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDropTarget(null);

    if (!dragData) {
      console.warn('Drop: no dragData');
      return;
    }

    const target = e.target.closest('[data-drop-target]');
    if (!target) {
      console.warn('Drop: no drop target found');
      return;
    }

    const targetMechKey = target.getAttribute('data-mech-key');
    const targetPosition = parseInt(target.getAttribute('data-position'), 10);
    const targetSlotLabel = target.getAttribute('data-slot-label');

    // Skip if dropping on same slot
    if (
      targetMechKey === dragData.fromMechKey &&
      targetPosition === dragData.fromPosition &&
      targetSlotLabel === dragData.fromSlotLabel
    ) {
      setDragData(null);
      return;
    }

    // Find the target mechanic name from mechanics list
    const targetMech = mechanics.find(m => (m.uid || m.name) === targetMechKey);

    const payload = {
      jobUid: dragData.booking.job_uid,
      targetMechanicUid: targetMech?.uid || targetMechKey,
      targetMechanicName: targetMech?.name || targetMechKey,
      targetPosition,
      targetSlotLabel,
    };
    console.log('Drop: reassigning', payload);
    reassignMutation.mutate(payload);

    setDragData(null);
  }, [dragData, mechanics, reassignMutation]);

  const handleDragEnd = useCallback(() => {
    setDragData(null);
    setDropTarget(null);
  }, []);

  return (
    <Layout>
      {/* Reassign Loading Overlay */}
      {reassignMutation.isPending && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-2xl px-8 py-6 flex items-center gap-4">
            <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
            <div>
              <p className="font-semibold text-brand-dark">Reassigning slot...</p>
              <p className="text-xs text-brand-neutral">Updating Zuper, sending confirmation</p>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <header className="bg-white border-b border-brand-light/60 p-3 md:p-4 flex justify-between items-center z-10 shadow-sm relative">
        <div className="flex items-center gap-2 md:gap-3">
          <div className="w-9 h-9 bg-gradient-to-br from-brand-light to-white rounded-xl flex items-center justify-center shadow-sm">
            <CalendarClock className="w-5 h-5 text-brand-primary" />
          </div>
          <div>
            <h1 className="text-base md:text-xl font-bold text-brand-dark tracking-tight">Slot Management</h1>
            <p className="text-xs text-brand-neutral hidden md:block">Technician schedule — drag to reassign slots</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {currentLocation && (
            <span className="hidden md:flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium text-brand-primary bg-brand-light/60 rounded-lg">
              <MapPin className="w-3 h-3" />
              {currentLocation.name}
            </span>
          )}
          <button
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium text-white bg-brand-primary hover:bg-brand-primary/90 disabled:opacity-50 transition-colors shadow-sm"
            title="Sync jobs from Zuper"
          >
            <Download className={`w-3.5 h-3.5 ${syncMutation.isPending ? 'animate-bounce' : ''}`} />
            {syncMutation.isPending ? 'Syncing...' : 'Sync Zuper'}
          </button>
          <button
            onClick={() => refetch()}
            className="p-2 rounded-xl text-brand-neutral hover:text-brand-primary hover:bg-brand-light transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-3 md:p-6 bg-[#f5f8f6]">
        <div className="max-w-[1600px] mx-auto space-y-4">

          {/* Toast */}
          {toast && (
            <div className={`p-3 rounded-xl text-sm border ${
              toast.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' :
              toast.type === 'warning' ? 'bg-amber-50 border-amber-200 text-amber-700' :
              'bg-red-50 border-red-200 text-red-700'
            }`}>
              {toast.message}
            </div>
          )}

          {/* Date Navigation */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setDateOffset(o => o - 1)}
              className="p-2 rounded-xl bg-white border border-brand-light/60 text-brand-neutral hover:text-brand-primary hover:bg-brand-light transition-colors shadow-sm"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setDateOffset(0)}
              className="px-4 py-2 rounded-xl bg-white border border-brand-light/60 text-sm font-medium text-brand-dark hover:bg-brand-light transition-colors shadow-sm"
            >
              Today
            </button>
            <button
              onClick={() => setDateOffset(o => o + 1)}
              className="p-2 rounded-xl bg-white border border-brand-light/60 text-brand-neutral hover:text-brand-primary hover:bg-brand-light transition-colors shadow-sm"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <div className="ml-2">
              <p className="text-sm font-bold text-brand-dark">{formatDateDisplay(dateStr)}</p>
              <p className="text-xs text-brand-neutral">{totalBookings} booking{totalBookings !== 1 ? 's' : ''} total</p>
            </div>
          </div>

          {isLoading ? (
            <div className="p-12 text-center text-brand-neutral text-sm">Loading...</div>
          ) : mechanics.length === 0 ? (
            <div className="p-12 text-center bg-white rounded-2xl border border-brand-light/60 shadow-sm">
              <User className="w-10 h-10 text-brand-light mx-auto mb-3" />
              <p className="text-brand-neutral text-sm font-medium">No mechanics configured</p>
              <p className="text-xs text-brand-neutral/60 mt-1">Add mechanics in Settings to view the slot grid</p>
            </div>
          ) : (
            <>
              {/* Unassigned bookings banner */}
              {unassignedBookings.length > 0 && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-medium text-amber-700">{unassignedBookings.length} unassigned booking{unassignedBookings.length !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {unassignedBookings.map(b => (
                      <span
                        key={b.job_uid}
                        className="text-xs bg-white border border-amber-200 px-2 py-1 rounded-lg text-amber-800"
                        title={b.assigned_mechanic_name ? `Assigned in Zuper to ${b.assigned_mechanic_name} — not in AMC mechanics config` : 'No technician assigned in Zuper'}
                      >
                        #{b.work_order_number} — {b.customer_name} ({b.slot_label || '?'})
                        {b.assigned_mechanic_name && (
                          <span className="ml-1.5 text-amber-600 font-medium">
                            → {b.assigned_mechanic_name}
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Technician columns */}
              <div
                className="grid gap-4"
                style={{ gridTemplateColumns: `repeat(${mechanics.length}, minmax(280px, 1fr))` }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onDragEnd={handleDragEnd}
              >
                {mechanics.map((mech) => {
                  const mechKey = mech.uid || mech.name;
                  const grid = slotGrid[mechKey] || { morning: [], afternoon: [] };
                  const morningBooked = grid.morning.filter(s => s.booking).length;
                  const afternoonBooked = grid.afternoon.filter(s => s.booking).length;
                  const totalSlots = grid.morning.length + grid.afternoon.length;
                  const totalMechBooked = morningBooked + afternoonBooked;

                  return (
                    <div key={mechKey} className="bg-white rounded-2xl border border-brand-light/60 shadow-sm overflow-hidden">
                      {/* Mechanic header */}
                      <div className="px-4 py-3 bg-gradient-to-r from-brand-light/40 to-white border-b border-brand-light/60">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-brand-primary/10 flex items-center justify-center">
                              <User className="w-4 h-4 text-brand-primary" />
                            </div>
                            <div>
                              <h3 className="text-sm font-bold text-brand-dark">{mech.name}</h3>
                              <p className="text-[11px] text-brand-neutral">{totalMechBooked}/{totalSlots} slots</p>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">AM {morningBooked}/{grid.morning.length}</span>
                            <span className="text-[10px] font-medium text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">PM {afternoonBooked}/{grid.afternoon.length}</span>
                          </div>
                        </div>
                      </div>

                      <div className="p-3 space-y-4">
                        {/* Morning */}
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-[10px] font-bold text-amber-600 uppercase tracking-wider">Morning</span>
                            <div className="flex-1 border-t border-brand-light/40" />
                          </div>
                          <div className="space-y-5">
                            {grid.morning.length > 0 ? grid.morning.map(slot => {
                              const isDropHere = dropTarget &&
                                dropTarget.mechKey === mechKey &&
                                dropTarget.position === slot.position &&
                                dropTarget.slotLabel === 'Morning';

                              return (
                                <div
                                  key={`m-${slot.position}`}
                                  className={`transition-all duration-150 rounded-xl ${
                                    isDropHere ? 'ring-2 ring-brand-primary ring-offset-2' : ''
                                  }`}
                                >
                                  <SlotCard
                                    slot={slot}
                                    mechKey={mechKey}
                                    slotLabel="Morning"
                                    onDragStart={handleDragStart}
                                    isDragging={dragData?.booking?.job_uid === slot.booking?.job_uid}
                                  />
                                </div>
                              );
                            }) : (
                              <p className="text-xs text-brand-neutral/40 text-center py-3">No morning positions</p>
                            )}
                          </div>
                        </div>

                        {/* Afternoon */}
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider">Afternoon</span>
                            <div className="flex-1 border-t border-brand-light/40" />
                          </div>
                          <div className="space-y-5">
                            {grid.afternoon.length > 0 ? grid.afternoon.map(slot => {
                              const isDropHere = dropTarget &&
                                dropTarget.mechKey === mechKey &&
                                dropTarget.position === slot.position &&
                                dropTarget.slotLabel === 'Afternoon';

                              return (
                                <div
                                  key={`a-${slot.position}`}
                                  className={`transition-all duration-150 rounded-xl ${
                                    isDropHere ? 'ring-2 ring-brand-primary ring-offset-2' : ''
                                  }`}
                                >
                                  <SlotCard
                                    slot={slot}
                                    mechKey={mechKey}
                                    slotLabel="Afternoon"
                                    onDragStart={handleDragStart}
                                    isDragging={dragData?.booking?.job_uid === slot.booking?.job_uid}
                                  />
                                </div>
                              );
                            }) : (
                              <p className="text-xs text-brand-neutral/40 text-center py-3">No afternoon positions</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </main>
    </Layout>
  );
}
